import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Flex, Text, SimpleGrid, Button, Badge, Spinner, Center, HStack,
  IconButton, Tooltip, useToast, Icon,
} from "@chakra-ui/react";
import { Phone, MessageSquare, RefreshCw, Sparkles, Contact } from "lucide-react";
import { getCrmOportunidades, criarCrmContato } from "../../services/api";
import { useSoftphone } from "../../contexts/softphone";
import {
  formatPhone, paraDiscagem, formatBRL, ehCNPJ, soDigitos, ROTULO_REGRA,
} from "./crmUtils";

const ACCENT = "#F68633";
const ORDEM = ["cnpj", "recorrente", "recompra", "ticket", "ml_exclusivo"];

export default function OportunidadesTab({ onAbrirChat, onAbrirFicha }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abrindo, setAbrindo] = useState(null);
  const sp = useSoftphone();
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const r = await getCrmOportunidades();
    if (r?.data) setData(r.data);
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const conversar = async (item) => {
    if (item.crm_contato_id) { onAbrirChat(item.crm_contato_id); return; }
    if (soDigitos(item.telefone).length < 10) {
      toast({ status: "warning", title: "Sem telefone válido", description: "Este cliente não tem telefone cadastrado." });
      return;
    }
    setAbrindo(item.cliente_id);
    const r = await criarCrmContato({ telefone: item.telefone, nome: item.nome, origem: "oportunidade" });
    setAbrindo(null);
    if (r?.data?.id) onAbrirChat(r.data.id);
    else toast({ status: "error", title: "Erro", description: r?.error || "Não foi possível abrir a conversa." });
  };

  const ligar = (item) => {
    if (soDigitos(item.telefone).length < 10) {
      toast({ status: "warning", title: "Sem telefone válido" });
      return;
    }
    if (!sp.registered) {
      toast({ status: "warning", title: "Ramal desconectado", description: "Conecte o softphone no Discador antes de ligar." });
      return;
    }
    sp.dial(paraDiscagem(item.telefone));
    toast({ status: "info", title: "Discando...", description: formatPhone(item.telefone) });
  };

  if (loading) return <Center py={16}><Spinner color={ACCENT} /></Center>;

  const secoes = ORDEM.filter((k) => data?.[k]);
  const vazio = secoes.every((k) => (data[k] || []).length === 0);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="sm" color="gray.500">Clientes que valem um contato proativo — ajuste as regras na aba Configurações.</Text>
        <Button size="sm" variant="ghost" leftIcon={<RefreshCw size={15} />} onClick={carregar}>Atualizar</Button>
      </Flex>

      {secoes.length === 0 || vazio ? (
        <Center py={16} flexDirection="column" color="gray.400">
          <Sparkles size={40} />
          <Text mt={3} fontSize="sm">Nenhuma oportunidade no momento.</Text>
          <Text fontSize="xs">Ative ou ajuste as regras em Configurações.</Text>
        </Center>
      ) : (
        secoes.map((regra) => {
          const itens = data[regra] || [];
          if (itens.length === 0) return null;
          return (
            <Box key={regra} mb={6}>
              <HStack mb={3}>
                <Icon as={Sparkles} color={ACCENT} boxSize={4} />
                <Text fontWeight="bold" fontSize="sm">{ROTULO_REGRA[regra]}</Text>
                <Badge colorScheme="orange" borderRadius="full">{itens.length}</Badge>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={3}>
                {itens.map((item) => (
                  <Box key={`${regra}-${item.cliente_id}`} bg="white" borderWidth="1px" borderColor="gray.100"
                    borderRadius="xl" p={4} boxShadow="xs" _hover={{ borderColor: ACCENT, boxShadow: "sm" }} transition="all 0.12s">
                    <Flex justify="space-between" align="start" mb={1}>
                      <Text fontWeight="semibold" fontSize="sm" noOfLines={1} flex="1">{item.nome || "Sem nome"}</Text>
                      {item.documento && (
                        <Badge colorScheme={ehCNPJ(item.documento) ? "purple" : "blue"} ml={2}>
                          {ehCNPJ(item.documento) ? "PJ" : "PF"}
                        </Badge>
                      )}
                    </Flex>
                    <Text fontSize="xs" color={ACCENT} fontWeight="medium" mb={2} noOfLines={1}>{item.motivo}</Text>
                    <HStack fontSize="xs" color="gray.500" spacing={4} mb={3}>
                      <Text>{item.qtd_pedidos} pedido(s)</Text>
                      <Text>{formatBRL(item.total_compras)}</Text>
                    </HStack>
                    <Flex justify="space-between" align="center">
                      <Text fontSize="xs" color="gray.500">{item.telefone ? formatPhone(item.telefone) : "sem telefone"}</Text>
                      <HStack spacing={1}>
                        <Tooltip label="Abrir ficha">
                          <IconButton aria-label="Abrir ficha" size="sm" variant="ghost" colorScheme="gray"
                            icon={<Contact size={16} />} onClick={() => onAbrirFicha(item.cliente_id)} />
                        </Tooltip>
                        <Tooltip label="Ligar">
                          <IconButton aria-label="Ligar" size="sm" variant="ghost" colorScheme="green"
                            icon={<Phone size={16} />} onClick={() => ligar(item)} />
                        </Tooltip>
                        <Button size="sm" leftIcon={<MessageSquare size={14} />} colorScheme="orange" variant="outline"
                          isLoading={abrindo === item.cliente_id} onClick={() => conversar(item)}>
                          Conversar
                        </Button>
                      </HStack>
                    </Flex>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>
          );
        })
      )}
    </Box>
  );
}
